import { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { FacetContext } from './Facet';

function getSelectedRows(selectedItems) {
  return (selectedItems ?? []).flatMap((element) => {
    if (element.children) {
      return element.children.map((record) => ({
        key: `${element.parent}-${record}`,
        parent: element.parent,
        label: record
      }));
    }

    return [
      {
        key: element.parent,
        label: element.parent
      }
    ];
  });
}

export const FacetBody = ({
  children,
  selectedItems,
  selectedCount,
  hoverTextonTitle,
  clearSelection,
  onRemoveItem,
  className,
  title,
  id,
  openByDefault,
  donotShowClearButtonOnOpen,
  toolTip,
  ...props
}) => {
  const { defaultActiveKey } = useContext(FacetContext);
  const [isExpanded, setIsExpanded] = useState(
    openByDefault || defaultActiveKey === id
  );
  const selectedRows = getSelectedRows(selectedItems);
  const filterCount = selectedCount ?? selectedRows.length;

  return (
    <Accordion
      className={className}
      variant="accordionFilterGroup"
      expanded={isExpanded}
      onChange={(event, expanded) => setIsExpanded(expanded)}
      {...props}
    >
      <AccordionSummary aria-controls={`${id}-content`} id={`${id}-header`}>
        <Box
          sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}
        >
          {/* Title + optional tooltip inline */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flex: 1,
              minWidth: 0
            }}
          >
            {hoverTextonTitle ? (
              <Tooltip title={hoverTextonTitle}>
                <Typography variant="h3">{title}</Typography>
              </Tooltip>
            ) : (
              <>
                <Typography variant="h3">{title}</Typography>
                {toolTip}
              </>
            )}
          </Box>

          {filterCount > 0 && (
            <Chip
              color="informational"
              label={filterCount}
              size="small"
              variant="badge"
            />
          )}

          {!donotShowClearButtonOnOpen && filterCount > 0 && (
            <IconButton
              size="small"
              aria-label="clearAll"
              onClick={(event) => {
                event.stopPropagation();
                clearSelection();
              }}
            >
              <fuel-icon name="close" />
            </IconButton>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails id={`${id}-content`}>
        {selectedRows.length > 0 && (
          <div>
            {selectedRows.map((item) => (
              <Box
                key={item.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 0.5
                }}
              >
                <Typography
                  title={
                    item.parent ? `${item.parent} - ${item.label}` : item.label
                  }
                  variant="bodySmall"
                  sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {item.parent && (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {item.parent} -{' '}
                    </Box>
                  )}
                  {item.label}
                </Typography>
                {onRemoveItem && (
                  <IconButton
                    aria-label={`remove-${item.label}`}
                    onClick={() => onRemoveItem(item)}
                    sx={{ p: 0.25 }}
                  >
                    <fuel-icon
                      name="close"
                      size="xs"
                      style={{ '--fuel-icon-size': '18px' }}
                    />
                  </IconButton>
                )}
              </Box>
            ))}
          </div>
        )}
        {children}
      </AccordionDetails>
    </Accordion>
  );
};

FacetBody.propTypes = {
  title: PropTypes.string.isRequired,

  id: PropTypes.string.isRequired,

  summary: PropTypes.node
};
